import { FC, useCallback, useMemo } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import isEmpty from 'lodash/isEmpty';

import { useDictionary } from '@cvt/hooks/useDictionary';
import { useUserCrud } from '@modules/Users/hooks/useUserCrud';

import { ObjectivesForm, FormData } from '../partials/ObjectivesForm/ObjectivesForm';


interface Props {
  open: boolean,
  onClose: () => void,
  options?: {
    user: Users.User
  };
}

export const EditCommunicationObjectivesDialog: FC<Props> = (props) => {
  const { open, onClose, options } = props;

  const { user } = options || {};

  const dictionary = useDictionary();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const defaultValues = useMemo(() => ({
    communicationObjective1: user?.preferences?.communicationObjective1 || '',
    communicationObjective2: user?.preferences?.communicationObjective2 || '',
    communicationObjective3: user?.preferences?.communicationObjective3 || '',
  }), [user?.preferences]);

  const { patchUser } = useUserCrud();

  const onSubmit = useCallback(async (data: FormData) => {
    await patchUser({
      id: user?.id,
      preferences: {
        ...data,
      },
    });

    onClose();
  }, [onClose, patchUser, user?.id]);

  if (isEmpty(user)) {
    onClose();
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <Box width="100%" px={4} py={4} position="relative">
        <Stack alignItems="center" justifyContent="space-between" direction="row" mb={2}>
          <Typography variant="h6" fontSize={18} fontWeight={600}>Edit Communication Objectives</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'text.secondary',
            }}
          >
            <Close/>
          </IconButton>
        </Stack>

        <Box>
          <ObjectivesForm
            defaultValues={defaultValues}
            onSubmitRequest={onSubmit}
            slots={{
              footer: (formState) => (
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} mt={4}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onClose}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    {dictionary.forms.buttonCancel}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    size="large"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    loading={formState.isSubmitting}
                  >
                    {dictionary.forms.buttonSave}
                  </LoadingButton>
                </Stack>
              ),
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
